import { forwardRef, Ref, useMemo } from "react"
import { Form, Formik, FormikProps } from "formik"
import * as Yup from "yup"
import { isPossiblePhoneNumber } from "libphonenumber-js"

import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { User } from "../../../../../users/userTypes"

import { CompanyUserFormMode, CompanyUserFormValues } from "../companyUserFormTypes"
import { getCompanyUserInitialValues } from "../companyUserFormMapper"
import { CreateEditUserContent } from "./CreateEditUserContent"

type CreateEditUserFormProps = Readonly<{
    mode: CompanyUserFormMode
    existingUser?: User
    onSubmit: (values: CompanyUserFormValues) => void
}>

type FormikRef = Ref<FormikProps<CompanyUserFormValues>>

export const CreateEditUserForm = forwardRef(
    ({ mode, existingUser, onSubmit }: CreateEditUserFormProps, ref: FormikRef) => {
        const { l10n } = useAppLocalization()

        const initialValues = useMemo(() => getCompanyUserInitialValues(existingUser), [existingUser])

        const validationSchema = useMemo(
            () =>
                Yup.object().shape({
                    employeeId: Yup.string(),
                    displayName: Yup.string().required(l10n.getString("company-user-validation-required")),
                    initials: Yup.string(),
                    username: Yup.string(),
                    email: Yup.string()
                        .email(l10n.getString("company-user-validation-invalid-email"))
                        .required(l10n.getString("company-user-validation-required")),
                    mobileNumber: Yup.string().test({
                        name: "is-valid-phone-number",
                        test: (value) => (value ? isPossiblePhoneNumber(value, "DK") : true),
                        message: l10n.getString("company-user-validation-invalid-phone"),
                    }),
                    phoneNumber: Yup.string().test({
                        name: "is-valid-phone-number",
                        test: (value) => (value ? isPossiblePhoneNumber(value, "DK") : true),
                        message: l10n.getString("company-user-validation-invalid-phone"),
                    }),
                    language: Yup.string(),
                    userGroupId: Yup.string(),
                    departmentId: Yup.string(),
                    approverForUserGroupIds: Yup.array(),
                    assignedPriceListIds: Yup.array(),
                    sendEmailInvitation: Yup.bool(),
                    disableUserSync: Yup.bool(),
                    isSecretary: Yup.bool(),
                    notifyOnGuestArrival: Yup.bool(),
                    isActive: Yup.bool(),
                    workgroups: Yup.array(),
                }),
            [l10n],
        )

        return (
            <Formik<CompanyUserFormValues>
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
                innerRef={ref}
            >
                <Form>
                    <CreateEditUserContent mode={mode} existingUser={existingUser} />
                </Form>
            </Formik>
        )
    },
)
